import { useState } from 'react';

const services = [
  {
    title: 'Edelsplitt',
    description: 'Hochwertiger Splitt aus natürlichem Gestein — ideal als pflegeleichte Bodendecker-Alternative für Vorgärten, Beete und Wege. Erhältlich in zahlreichen Farben und Körnungen.',
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/edelsplitt-800x800.jpg',
    icon: 'ri-landscape-line',
    features: ['Diverse Körnungen', 'Unkrautunterdrückend', 'Wartungsarm', 'Langlebig'],
  },
  {
    title: 'Gabionensteine',
    description: 'Robuste Füllsteine für Gabionen-Körbe — schaffen imposante Sichtschutzwände, Gartenmauern und Terrassen-Einfassungen mit natürlichem Charakter.',
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/gabionensteine-800x800.jpg',
    icon: 'ri-stack-line',
    features: ['Stabiler Verbund', 'Frostbeständig', 'Alle Steingrößen', 'Individuell füllbar'],
  },
  {
    title: 'Dekosteine',
    description: 'Edle Natursteine als gestalterische Akzente — für Gartenpfade, Teichrandgestaltung, Pflanzbeete und kreative Landschaftsprojekte jeder Art.',
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/dekosteine.jpg',
    icon: 'ri-flower-line',
    features: ['Hochglanz-Optik', 'Einzigartige Farben', 'Handverlesen', 'Formstabil'],
  },
  {
    title: 'Zubehör',
    description: 'Alles für die perfekte Steingestaltung: Vlies, Randsteine, Werkzeug und Befestigungsmaterial — damit Ihr Projekt von Anfang bis Ende reibungslos verläuft.',
    image: 'https://ziersteine.de/wp-content/uploads/2019/02/zubehoer-500x500.jpg',
    icon: 'ri-tools-line',
    features: ['Unkrautvlies', 'Randsteine', 'Profiwerkzeug', 'Verlegehilfen'],
  },
];

export default function ServicesSection() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section id="services" className="py-32 px-6" style={{ backgroundColor: '#F5F5F7' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-4">
          <div>
            <span className="px-4 py-1 rounded-full border border-stone-300 text-stone-500 text-xs tracking-widest uppercase mb-4 inline-block">
              Unser Sortiment
            </span>
            <h2
              className="font-light text-stone-900 leading-tight"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontFamily: "'Playfair Display', serif" }}
            >
              Unsere Leistungen
            </h2>
          </div>
          <p className="text-stone-500 text-sm max-w-xs leading-relaxed">
            Von der Planung bis zur vollendeten Gartengestaltung — alles aus einer Hand.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, i) => (
            <div
              key={service.title}
              className={`group rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                hoveredIndex === i ? 'shadow-xl -translate-y-1' : 'shadow-none'
              }`}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white font-semibold text-lg uppercase tracking-widest">{service.title}</h3>
                </div>
              </div>

              {/* Body */}
              <div
                className={`p-6 transition-all duration-300 ${
                  hoveredIndex === i ? 'bg-stone-900 text-white' : 'bg-white text-stone-700'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-full mb-4 transition-colors duration-300 ${
                  hoveredIndex === i ? 'bg-white/15' : 'bg-stone-100'
                }`}>
                  <i className={`${service.icon} text-base ${hoveredIndex === i ? 'text-white' : 'text-stone-700'}`} />
                </div>
                <p className={`text-xs leading-relaxed mb-5 transition-colors duration-300 ${
                  hoveredIndex === i ? 'text-stone-300' : 'text-stone-500'
                }`}>
                  {service.description}
                </p>
                <ul className="space-y-1">
                  {service.features.map((f) => (
                    <li key={f} className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
                      hoveredIndex === i ? 'text-stone-300' : 'text-stone-500'
                    }`}>
                      <span className={`w-1 h-1 rounded-full flex-shrink-0 ${hoveredIndex === i ? 'bg-stone-400' : 'bg-stone-400'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Banner */}
        <div
          className="mt-16 rounded-2xl overflow-hidden relative"
          style={{ height: '260px' }}
        >
          <img
            src="https://ziersteine.de/wp-content/uploads/2019/02/background-grey.jpg"
            alt="Hintergrund"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <p className="text-white/60 text-sm mb-2 tracking-widest uppercase">Ihr Haus und Hof sind es wert,</p>
            <p
              className="text-white font-light leading-tight"
              style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontFamily: "'Playfair Display', serif" }}
            >
              höchste Beachtung zu genießen
            </p>
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-8 px-7 py-3 bg-white text-stone-900 rounded-full text-sm font-medium hover:bg-stone-100 transition-all cursor-pointer whitespace-nowrap"
            >
              Jetzt Angebot anfragen
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
